/**
 * Section "Attestations FTPE" avec édition de date d'adhésion.
 * Extrait de DocumentSections.js (refactor P2 — Feb 2026).
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { DatePickerFr } from '../ui/DatePickerFr';
import {
  Award, Shield, Clock, Calendar, Edit2, Save, Loader2,
  Eye, Download, Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadDocument, viewDocument } from '../../services/downloadService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FtpeSection({ items, membreId, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [dateValue, setDateValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!dateValue || !membreId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/membres/${membreId}/ftpe-date-adhesion`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ date_adhesion: dateValue }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.pdf_regenerated ? 'Date modifiée et attestation régénérée' : 'Date modifiée');
        setEditingId(null);
        if (onRefresh) onRefresh();
      } else {
        toast.error(data.detail || 'Erreur lors de la modification');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setSaving(false); }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'signee':
        return { label: 'Signée', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: <Shield className="h-3 w-3" /> };
      case 'non_signee':
        return { label: 'Non signée', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', icon: <Clock className="h-3 w-3" /> };
      default:
        return { label: 'En cours', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', icon: <Clock className="h-3 w-3" /> };
    }
  };

  if (!items?.length) return null;

  const sorted = [...items].sort((a, b) => {
    const order = { signee: 0, non_signee: 1, en_cours: 2 };
    return (order[a.document_status] ?? 3) - (order[b.document_status] ?? 3);
  });

  return (
    <div>
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Award className="h-4 w-4 text-amber-500" />
        Attestations FTPE ({items.length})
        <span className="text-xs font-normal text-gray-500">(jusqu&apos;à échéance)</span>
      </h4>
      <div className="space-y-2">
        {sorted.map((att) => {
          const statusCfg = getStatusConfig(att.document_status);
          const canView = att.url || att.adhesion_id;
          const canFinalize = att.peut_finaliser || att.can_resume;

          return (
            <div key={att.id} className={`p-3 rounded-lg border ${att.document_status === 'signee' ? 'bg-green-50 border-green-200' : att.document_status === 'non_signee' ? 'bg-yellow-50 border-yellow-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`ftpe-doc-${att.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg shadow-sm ${att.document_status === 'signee' ? 'bg-green-100' : 'bg-white'}`}>
                    <Award className={`h-4 w-4 ${att.document_status === 'signee' ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{att.nom || 'Attestation FTPE'}</p>
                    <p className="text-xs text-gray-500">
                      {att.date_adhesion && `Début : ${new Date(att.date_adhesion).toLocaleDateString('fr-FR')}`}
                      {att.date_expiration && (
                        <span className="ml-2 font-medium text-amber-700">
                          • Échéance : {new Date(att.date_expiration).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </p>
                    <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`} data-testid={`ftpe-status-${att.id}`}>
                      {statusCfg.icon} {statusCfg.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {membreId && att.document_status === 'signee' && (
                    <Button variant="outline" size="sm" data-testid={`edit-ftpe-date-${att.id}`} className="border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => { setEditingId(editingId === att.id ? null : att.id); setDateValue(att.date_adhesion ? att.date_adhesion.substring(0, 10) : ''); }}>
                      <Edit2 className="h-4 w-4 mr-1" /> Modifier date
                    </Button>
                  )}
                  {canFinalize && att.document_status !== 'signee' && (
                    <Button size="sm" data-testid={`finalize-ftpe-${att.id}`} className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => {
                        const url = att.adhesion_id ? `/adhesion-ftpe?adhesion_id=${att.adhesion_id}` : '/adhesion-ftpe';
                        window.location.href = url;
                      }}>
                      <Upload className="h-3.5 w-3.5 mr-1" /> Finaliser
                    </Button>
                  )}
                  {canView && att.document_status === 'signee' && (
                    <>
                      <Button variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-100" onClick={() => viewDocument(att)}>
                        <Eye className="h-4 w-4 mr-1" /> Voir
                      </Button>
                      <Button variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-100" onClick={() => downloadDocument(att)}>
                        <Download className="h-4 w-4 mr-1" /> Télécharger
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {editingId === att.id && (
                <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-3 flex-wrap">
                  <Calendar className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-gray-700 font-medium">Nouvelle date :</span>
                  <DatePickerFr id={`ftpe-date-input-${att.id}`}
                    className="text-sm"
                    value={dateValue} onChange={(v) => setDateValue(v)} />
                  <Button size="sm" data-testid={`save-ftpe-date-${att.id}`} className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handleSave} disabled={saving || !dateValue}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                    Enregistrer & Régénérer
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Annuler</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
